export type KeyboardShortcutGroup = 'general' | 'regionSelection' | 'stacks';

export type KeyboardShortcut = {
  keys: string;
  descriptionKey: string;
  group: KeyboardShortcutGroup;
};

/**
 * Ordered list of groups that controls render order in shortcuts-popup.
 * Most-used first (general), then section-specific (regionSelection, stacks).
 */
export const KEYBOARD_SHORTCUT_GROUPS: ReadonlyArray<KeyboardShortcutGroup> = [
  'general',
  'regionSelection',
  'stacks',
];

export const KEYBOARD_SHORTCUT_GROUP_LABEL_KEYS = {
  general: 'shortcuts.group.general',
  regionSelection: 'shortcuts.group.regionSelection',
  stacks: 'shortcuts.group.stacks',
} as const satisfies Record<KeyboardShortcutGroup, string>;

export const KEYBOARD_SHORTCUTS = [
  // General - always available, highest frequency.
  { keys: 'SPACE', descriptionKey: 'shortcuts.pan', group: 'general' },
  { keys: '+/-', descriptionKey: 'shortcuts.zoom', group: 'general' },
  { keys: 'Cmd+Z', descriptionKey: 'shortcuts.undo', group: 'general' },
  { keys: 'Shift+Cmd+Z', descriptionKey: 'shortcuts.redo', group: 'general' },
  // Region selection - inside Region tab, multi-select + rename.
  { keys: 'Shift+Click', descriptionKey: 'shortcuts.rangeSelect', group: 'regionSelection' },
  { keys: 'Cmd+Click', descriptionKey: 'shortcuts.toggleSelect', group: 'regionSelection' },
  { keys: 'Shift+Arrow', descriptionKey: 'shortcuts.extendSelect', group: 'regionSelection' },
  { keys: 'Cmd+Space', descriptionKey: 'shortcuts.toggleFocused', group: 'regionSelection' },
  { keys: 'F2 / Enter', descriptionKey: 'shortcuts.rename', group: 'regionSelection' },
  // Stacks - create/disband from selection.
  { keys: 'Cmd+G', descriptionKey: 'shortcuts.createStack', group: 'stacks' },
  { keys: 'Shift+Cmd+G', descriptionKey: 'shortcuts.disbandStack', group: 'stacks' },
] as const satisfies readonly KeyboardShortcut[];

export type ShortcutDescriptionKey =
  (typeof KEYBOARD_SHORTCUTS)[number]['descriptionKey'];

export function getShortcutKeys(
  descriptionKey: ShortcutDescriptionKey,
): string {
  const shortcut = KEYBOARD_SHORTCUTS.find(
    (item) => item.descriptionKey === descriptionKey,
  );
  if (!shortcut) {
    throw new Error(`Unknown keyboard shortcut: ${descriptionKey}`);
  }
  return shortcut.keys;
}

export function getPrimaryShortcutLetter(
  descriptionKey: ShortcutDescriptionKey,
): string {
  const lastToken = getShortcutKeys(descriptionKey)
    .split('+')
    .at(-1)
    ?.trim()
    .toLowerCase();
  if (!lastToken || lastToken.length !== 1) {
    throw new Error(
      `Keyboard shortcut is not a single-letter primary chord: ${descriptionKey}`,
    );
  }
  return lastToken;
}
